import { Modal } from "react-bootstrap";
import { cents, showMoney } from "./Data/Money";
import { CostProjectionDto, ParkerCostDto } from "./CostProjectionSection";
import "./CostProjectionDetailsModal.scss";

type Props = {
  show: boolean;
  onHide: () => void;
  title: string;
  data: CostProjectionDto;
};

export function CostProjectionDetailsModal({
  show,
  onHide,
  title,
  data,
}: Props) {
  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body className="d-flex flex-column gap-4">
        {data.groups
          .filter((group) => group.parkerCount > 0)
          .map((group) => {
            const groupTotal = group.subtotal;
            const hasParkerCosts = group.parkerCosts?.length > 0;

            return (
              <div key={group.groupId} className="rounded mb-3 overflow-hidden">
                <div className="bg-dark text-white px-3 py-2">
                  <div className="d-flex justify-content-between fw-semibold">
                    <div>{group.groupName}</div>
                    <div className="small">
                      {group.parkerCount} {group.groupId === null ? "space" : "parker"}
                      {group.parkerCount !== 1 && "s"}
                    </div>
                  </div>
                </div>

                <div className="border border-top-0 rounded-bottom p-3 d-flex flex-column gap-3">
                  {hasParkerCosts ? (
                    <div className="parker-costs-breakdown">
                      <table className="table table-sm mb-0 small">
                        <thead>
                          <tr className="text-muted">
                            <th className="fw-semibold border-0 ps-0">Parker</th>
                            <th className="fw-semibold border-0 text-end">Base</th>
                            <th className="fw-semibold border-0 text-end">Adj.</th>
                            <th className="fw-semibold border-0 text-end pe-0">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.parkerCosts.map((pc) => (
                            <ParkerCostRow key={pc.subscriptionParkerId} parkerCost={pc} />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    (() => {
                      const baseLineItem = group.lineItems.find(li => li.description === "Base Price");
                      const unitPrice = baseLineItem?.unitPrice;
                      return unitPrice ? (
                        <div className="small text-muted">
                          <span className="fw-semibold">{group.parkerCount} space{group.parkerCount !== 1 ? "s" : ""}</span>
                          <span className="ms-1">× {showMoney(cents(unitPrice))} each</span>
                        </div>
                      ) : null;
                    })()
                  )}

                  {group.appliedTiers?.length > 0 && (
                    <div className="bg-white px-3 py-2 small border-top border-bottom">
                      {group.appliedTiers.map((tier: any, idx: number) => (
                        <div
                          key={idx}
                          className="d-flex justify-content-between text-dark"
                        >
                          <div className="fw-semibold">
                            {tier.label}
                            <span className="text-muted ms-1">
                              ({tier.quantity})
                            </span>
                          </div>

                          <div
                            className={`fw-semibold ${
                              tier.total < 0 ? "text-success" : "text-danger"
                            }`}
                          >
                            {showMoney(cents(tier.total))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="d-flex justify-content-between fw-semibold px-3 py-2 rounded bg-danger-subtle text-danger">
                    <div>Group Total</div>
                    <div>{showMoney(cents(groupTotal))}</div>
                  </div>
                </div>
              </div>
            );
          })}

        <div className="d-flex justify-content-between align-items-center fs-5 fw-bold bg-danger-subtle text-danger px-3 py-3 rounded">
          <div>Period Total</div>
          <div>{showMoney(cents(data.total.amount!))}</div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

function ParkerCostRow({ parkerCost }: { parkerCost: ParkerCostDto }) {
  const { parkerName, startDate, basePrice, adjustment, finalPrice, wasProrated } = parkerCost;

  return (
    <tr className={wasProrated ? "text-muted" : ""}>
      <td className="border-0 ps-0">
        <span className="fw-semibold">{parkerName}</span>
        {startDate && wasProrated && (
          <span className="text-muted ms-1 small">
            (starts {startDate})
          </span>
        )}
        {wasProrated && (
          <span className="badge bg-secondary ms-1 small">prorated</span>
        )}
      </td>
      <td className="border-0 text-end">
        {showMoney(cents(basePrice))}
      </td>
      <td className={`border-0 text-end ${adjustment < 0 ? "text-success" : adjustment > 0 ? "text-danger" : ""}`}>
        {adjustment !== 0 ? showMoney(cents(adjustment)) : "—"}
      </td>
      <td className="border-0 text-end fw-semibold pe-0">
        {showMoney(cents(finalPrice))}
      </td>
    </tr>
  );
}
